import { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import styled from "styled-components";
import type { Pog } from "@CommonTypes/merchflow/pog/pog";
import { PlanogramItem } from "@CommonTypes/merchflow/pog/item";
import { useAtomValue } from "jotai";
import {
  planogramHoveredContainerAtom,
  planogramHoveredItemAtom,
  planogramHoveredPegboardPlacementAtom,
  planogramHoveredShelfAtom,
} from "../store/atoms";
import { Flex, Text } from "src/elements";
import { useTranslation } from "src/i18n";
import {
  itemFrontOverhangMarker,
  shelfFrontOverhangMarker,
} from "src/components/Planogram/common/frontOverhangView";
import { ItemDetails, type ItemHoverOverrides } from "./components/ItemDetails";
import { ContainerDetails } from "./components/ContainerDetails";
import { ShelfDetails } from "./components/ShelfDetails";
import { relaxedItemsAtom } from "src/components/Planogram/store/atoms";
import { RelaxedItemConstraint } from "@CommonTypes/merchflow/pog/pog";
import { weightOptionsForHover } from "src/components/Planogram/common/shelfWeight";
import { useApi } from "src/api";
import { ResponseGetMerchantSettings } from "@CommonApi/merchant-settings";
import { useAsyncState } from "src/hooks/useAsyncState";
import {
  toPegboardPlacementView,
  toPlanogramItemView,
} from "src/components/Planogram/common/planogramItemView";

const ReviewReadout = styled.div`
  position: absolute;
  bottom: 8px;
  left: 8px;
  width: min(520px, calc(100% - 16px));
  z-index: 6;
  padding: 8px 10px;
  background: white;
  border: 1px solid #e2e9e7;
  border-radius: 10px;
  box-shadow: 0 2px 12px #172b3314;
  pointer-events: none;
  overflow: visible;
`;

export const PlanogramHovererDetails = ({
  review = false,
  itemOverrides,
  sourcePog,
  obstruction,
  inspectionPaused = false,
  sourceLabel,
  comparisonBefore,
}: {
  review?: boolean;
  itemOverrides?: ItemHoverOverrides;
  sourcePog?: Pog | null;
  obstruction?: HTMLElement | null;
  inspectionPaused?: boolean;
  sourceLabel?: string;
  comparisonBefore?: Pog | null;
} = {}) => {
  const readoutRef = useRef<HTMLDivElement>(null);
  const [bottom, setBottom] = useState(8);
  const [top, setTop] = useState(8);
  const hoveredShelfItemSource = useAtomValue(planogramHoveredItemAtom);
  const hoveredPegboardSource = useAtomValue(planogramHoveredPegboardPlacementAtom);
  const hoveredContainerSource = useAtomValue(planogramHoveredContainerAtom);
  const hoveredShelfSource = useAtomValue(planogramHoveredShelfAtom);
  const reboundItem =
    sourcePog !== undefined && hoveredShelfItemSource
      ? sourcePog?.getItem(hoveredShelfItemSource.uniqueId)
      : hoveredShelfItemSource;
  const hoveredShelfItem =
    sourcePog === undefined
      ? hoveredShelfItemSource
      : reboundItem instanceof PlanogramItem
        ? reboundItem
        : null;
  const hoveredPegboardPlacement =
    sourcePog !== undefined && hoveredPegboardSource
      ? (sourcePog?.planogram.getPegboardPlacement(hoveredPegboardSource.uniqueId) ?? null)
      : hoveredPegboardSource;
  const hoveredContainer =
    sourcePog !== undefined && hoveredContainerSource
      ? (sourcePog?.containers.find(
          (container) => container.uniqueId === hoveredContainerSource.uniqueId,
        ) ?? null)
      : hoveredContainerSource;
  const hoveredShelf =
    sourcePog !== undefined && hoveredShelfSource
      ? (sourcePog?.getShelf(hoveredShelfSource.uniqueId) ?? null)
      : hoveredShelfSource;
  const relaxedItems = useAtomValue(relaxedItemsAtom);
  const { t } = useTranslation("common");

  const { getMerchantSettingsApi } = useApi();
  const [merchantSettings, setMerchantSettings] = useAsyncState<ResponseGetMerchantSettings>();

  useEffect(() => {
    setMerchantSettings(getMerchantSettingsApi());
  }, []);

  const relaxedItemsMapping = useMemo(
    () =>
      relaxedItems?.reduce(
        (total, cur) => {
          total[cur.entity.itemId] = cur;
          return total;
        },
        {} as Record<string, RelaxedItemConstraint>,
      ),
    [relaxedItems],
  );

  const showMagnitude = Boolean(relaxedItems?.some((item) => item.magnitude !== "fully_relaxed"));
  const dosDisplayAsPercentage = Boolean(merchantSettings?.dosPercentage);
  const isShelfWeightEnabled = Boolean(merchantSettings?.shelfWeightEnabled);
  const hoveredViewItem = hoveredShelfItem
    ? toPlanogramItemView(hoveredShelfItem)
    : hoveredPegboardPlacement
      ? toPegboardPlacementView(hoveredPegboardPlacement)
      : null;
  const hoveredPlanogram =
    hoveredShelfItemSource?.shelf?.bay.planogram ??
    hoveredPegboardSource?.pegboard.bay.planogram ??
    hoveredShelfSource?.bay.planogram;
  const sourceLabelKey = !comparisonBefore
    ? "planogramHover.source.saved"
    : hoveredPlanogram === comparisonBefore.planogram
      ? "planogramHover.source.before"
      : "planogramHover.source.after";
  const shownLabel = sourceLabel ?? t(sourceLabelKey);
  const placeAbove =
    !!comparisonBefore && sourceLabelKey === "planogramHover.source.after" && !!hoveredViewItem;
  const weightOptions = weightOptionsForHover(hoveredShelfItem, hoveredPegboardPlacement);
  const overhang = hoveredShelfItem
    ? itemFrontOverhangMarker(hoveredShelfItem)
    : hoveredShelf
      ? shelfFrontOverhangMarker(hoveredShelf)
      : null;
  useLayoutEffect(() => {
    const readout = readoutRef.current;
    const canvas = readout?.parentElement;
    if (!review || !readout || !canvas) return;
    const fit = () => {
      const canvasBounds = canvas.getBoundingClientRect();
      const visibleBottom = Math.min(
        window.innerHeight,
        obstruction?.getBoundingClientRect().top ?? window.innerHeight,
      );
      setBottom(Math.max(8, Math.ceil(canvasBounds.bottom - visibleBottom + 8)));
      const headerBottom =
        canvas.querySelector("[data-review-canvas-header]")?.getBoundingClientRect().bottom ??
        canvasBounds.top;
      const desiredTop = Math.max(0, canvasBounds.top, headerBottom) - canvasBounds.top + 8;
      const latestTop = Math.max(
        8,
        visibleBottom - canvasBounds.top - readout.getBoundingClientRect().height - 8,
      );
      setTop(Math.ceil(Math.min(desiredTop, latestTop)));
    };
    fit();
    const observer = new ResizeObserver(fit);
    observer.observe(canvas);
    observer.observe(readout);
    if (obstruction) observer.observe(obstruction);
    window.addEventListener("resize", fit);
    window.addEventListener("scroll", fit, true);
    return () => {
      observer.disconnect();
      window.removeEventListener("resize", fit);
      window.removeEventListener("scroll", fit, true);
    };
  }, [
    review,
    hoveredViewItem?.uniqueId,
    hoveredShelf?.uniqueId,
    hoveredContainer?.uniqueId,
    obstruction,
    placeAbove,
  ]);

  const details = (
    <Flex
      minHeight="24px"
      gap="5px"
      column={review}
      align={review ? "stretch" : "center"}
      overflow={review ? "visible" : "hidden"}
    >
      {review && hoveredViewItem && <Text variant="small2">{shownLabel}</Text>}
      {overhang && <Text variant="caption1">{overhang.description}</Text>}
      {hoveredViewItem && (
        <ItemDetails
          item={hoveredViewItem}
          relaxedItemsMapping={
            hoveredViewItem.kind === "SHELF_ITEM" ? relaxedItemsMapping : undefined
          }
          showMagnitude={hoveredViewItem.kind === "SHELF_ITEM" && showMagnitude}
          dosDisplayAsPercentage={dosDisplayAsPercentage}
          isShelfWeightEnabled={isShelfWeightEnabled}
          weightOptions={weightOptions}
          overrides={itemOverrides}
          review={review}
        />
      )}
      {review && inspectionPaused && hoveredViewItem && (
        <Text variant="small2">{t("planogramHover.inspectionPaused")}</Text>
      )}

      {hoveredContainer && <ContainerDetails container={hoveredContainer} />}

      {hoveredShelf && (
        <ShelfDetails shelf={hoveredShelf} isShelfWeightEnabled={isShelfWeightEnabled} />
      )}
    </Flex>
  );
  if (!review) return details;
  if (!hoveredViewItem && !hoveredContainer && !hoveredShelf) return null;
  return (
    <ReviewReadout
      ref={readoutRef}
      style={placeAbove ? { top, bottom: "auto" } : { bottom }}
      data-position={placeAbove ? "top" : "bottom"}
      role="tooltip"
      aria-label={t("planogramHover.detailsLabel")}
    >
      {details}
    </ReviewReadout>
  );
};
